import React, { useCallback, useState } from "react";
import { Table, Button, Form, Row, Col } from "react-bootstrap";
import ErrorComponent from "components/shared/ErrorComponent";
import Loader from "components/shared/Loader";
import { useDeleteWorkbookMutation, useGetWorkbooksQuery } from "store/services/workbook.service";
import { confirmPopup, formatDate } from "helpers/common";
import { Link, useNavigate, useParams } from "react-router-dom";
import { debounce } from "lodash";
import toast from "react-hot-toast";

const WorkbooksList = () => {
  const {_id: currentWorkBookId} = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const limit = 10;

  const [deleteWorkbook, { isLoading: deleteWorkbookLoading }] = useDeleteWorkbookMutation();

  // Fetch data for the current page
  const { data, isLoading, isFetching, isError } = useGetWorkbooksQuery({
    skip: page,
    limit,
    search
  });

  const { items: workbooks = [], totalPages = 0 } = data || {};

  const handleSearch = useCallback(debounce((e) => {
    setSearch(e.target.value);
    setPage(1);
  }, 500), [])

  const handleDelete = async (workbookId) => {
    try {
      let confirm = await confirmPopup();
      if(!confirm) return;
      
      await toast.promise(deleteWorkbook(workbookId).unwrap(), {
        loading: "Please wait...",
        success: "Success"
      });

      if(workbookId === currentWorkBookId) navigate("/home");
    } catch(err) {
      console.error("Error deleting workbook", err);
    }
  }

  return (
    <>
      <div className="filters py-2 mb-2">
        <Row className="align-items-center">
          <Col lg="9">
            <Form.Control
              type="search"
              placeholder="Search workbooks"
              onChange={handleSearch}
            />
          </Col>
          <Col lg="3">
            {!isLoading && isFetching && (
              <Loader
                size={20}
              />
            )}
          </Col>
        </Row>
      </div>
      {isLoading ? (
        <div className="d-flex justify-content-center pb-0 pt-2">
          <Loader />
        </div>
      ) : isError ? (
        <ErrorComponent />
      ) : workbooks?.length ? (
        <>
          <Table responsive style={{ width: "100%", tableLayout: "fixed" }}>
            <thead>
              <tr>
                <th className="border-0 bg-transparent p-1 fw-normal" style={{ width: "50%" }}>
                  Title
                </th>
                <th className="border-0 bg-transparent p-1 fw-normal" style={{ width: "35%" }}>
                  Created At
                </th>
                <th className="border-0 bg-transparent p-1 fw-normal" style={{ width: "15%" }}>
                </th>
              </tr>
            </thead>

            <tbody>
              {workbooks.map((workbook) => (
                <tr key={workbook._id}>
                  <td className="border-0 bg-transparent p-1 wrap" title={workbook?.toolName}>
                    <Link
                      to={`/workbook/${workbook._id}`}
                      className="d-flex align-items-start themeClr"
                      reloadDocument
                    >
                      <p className="m-0 text-truncate">{workbook?.toolName}</p>
                    </Link>
                  </td>
                  <td className="border-0 bg-transparent p-1" title={formatDate(workbook.createdAt)}>
                    <p className="m-0">{formatDate(workbook.createdAt)}</p>
                  </td>
                  <td className="border-0 bg-transparent p-1">
                  <Button
                    className="border-0 p-1 themeClr"
                    variant="transparent"
                    onClick={() => handleDelete(workbook._id)}
                  >
                    <i class="fa fa-trash-o" aria-hidden="true"></i>
                  </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination Controls */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <Button
              className="border-0"
              variant="transparent"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
            >
              <i class="fa fa-arrow-left" aria-hidden="true"></i>
            </Button>
            <span>
              Page {page} of {totalPages}
            </span>
            <Button
              className="border-0"
              variant="transparent"
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page === totalPages}
            >
              <i class="fa fa-arrow-right" aria-hidden="true"></i>
            </Button>
          </div>
        </>
      ) : (
        "No workbooks found"
      )}
    </>
  );
};

export default React.memo(WorkbooksList);
